import { SmartGoals, SmartGoalsResponse, BackendResponse } from "../models/api-response";
import { AxiosRequestConfig } from "axios";
import { callExternalApi, ApiResponse } from "./external-api.service";
import { getLogger } from "src/utils/logger";
import { BehaviourSupportPlanData, PersonalisedLearningSupportPlanData } from "src/models/form-types";

const logger = getLogger('smart-goals.service');
const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;

/**
 * Generate SMART goals based on the provided form data
 *
 * @param formData The behavior support plan or personalised learning support plan form data
 * @param accessToken The user's access token for authentication
 * @returns A promise resolving to an ApiResponse containing the SMART goals or an error
 */
export const generateSmartGoals = async (
  formData: BehaviourSupportPlanData | PersonalisedLearningSupportPlanData | any,
  numGoals: number,
  accessToken: string
): Promise<ApiResponse<SmartGoalsResponse>> => {
  const config: AxiosRequestConfig = {
    url: `${apiServerUrl}/api/documents/smartGoals`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    data: {formData, numGoals},
  };
  
  logger.debug("Generating SMART goals");
  const response = await callExternalApi<BackendResponse>({ config });
  logger.debug(`Response received with status code: ${response.data?.status || 'unknown'}`);
  
  if (response.error || !response.data) {
    return {
      data: null,
      error: response.error || { message: "Failed to generate smart goals" }
    };
  }
  
  try {
    // Extract content from the backend response
    const { content, document_id } = response.data;
    
    // Parse content if it's a string, otherwise use as is
    const goalContent: SmartGoals = typeof content === 'string' 
      ? JSON.parse(content) 
      : content;
    
    // Log safely without risking circular references
    logger.debug(`Goal content received with ${goalContent.smart_goals?.length || 0} goals`);
    
    return {
      data: {
        goals: goalContent.smart_goals || [],
        documentId: document_id
      },
      error: null
    };
  } catch (parseError) {
    logger.error("Failed to parse content:", parseError);
    return {
      data: null,
      error: { message: "Invalid response format from API" }
    };
  }
};
